/**
 * @license Copyright (c) 2003-2022, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

/**
 * @module media-embed/mediaembedui
 */

import {Plugin} from 'ckeditor5/src/core';
import {createDropdown} from 'ckeditor5/src/ui';


import CustomImageAlternativeTextFormView from "./custom-image-alternative-text-form-view";
import {iconsMap} from "./blocks-icons-map";

/**
 * The media embed UI plugin.
 *
 * @extends module:core/plugin~Plugin
 */
export default class CustomImageAlternativeTextUI extends Plugin {
    /**
     * @inheritDoc
     */
    static get requires() {
        return [];
    }

    /**
     * @inheritDoc
     */
    static get pluginName() {
        return 'CustomImageAlternativeTextUI';
    }

    /**
     * @inheritDoc
     */
    init() {
        const editor = this.editor;
        const command = editor.commands.get('customImageAlternativeText');

        editor.ui.componentFactory.add('customImageAlternativeText', locale => {
            const dropdown = createDropdown(locale);
            const alternativeForm = new CustomImageAlternativeTextFormView(getFormValidators(editor.t), editor.locale);

            this._setUpDropdown(dropdown, alternativeForm, command, editor);
            this._setUpForm(dropdown, alternativeForm, command);

            return dropdown;
        });
    }

    _setUpDropdown(dropdown, form, command) {
        const editor = this.editor;
        const t = editor.t;
        const button = dropdown.buttonView;
        // dropdown.bind( 'isOn', 'isEnabled' ).to( command, 'value', 'isEnabled' );
        dropdown.panelView.children.add(form);

        button.set({
            label: t('Alternative text'),
            icon: iconsMap.get('alternative'),
            tooltip: true,
            withText: false
        });


        // Note: Use the low priority to make sure the following listener starts working after the
        // default action of the drop-down is executed (i.e. the panel showed up). Otherwise, the
        // invisible form/input cannot be focused/selected.
        button.on('open', () => {
            form.disableCssTransitions();

            // Make sure that each time the panel shows up, the URL field remains in sync with the value of
            // the command. If the user typed in the input, then canceled (`urlInputView#fieldView#value` stays
            // unaltered) and re-opened it without changing the value of the media command (e.g. because they
            // didn't change the selection), they would see the old value instead of the actual value of the
            // command.
            form.url = command.value || '';
            form.urlInputView.fieldView.select();
            form.enableCssTransitions();
        }, {priority: 'low'});

        dropdown.on('submit', () => {
            if (form.isValid()) {
                button.set({isOn: true})
                editor.execute('customImageAlternativeText', form.url);
                editor.editing.view.focus();
            }
        });

        dropdown.on('change:isOpen', () => form.resetFormStatus());
        dropdown.on('cancel', () => {
            editor.editing.view.focus();
        });

        command.on('change:value', (evt, propertyName, newValue, oldValue) => {
            button.set({isOn: !!newValue})
        })
    }

    /**
     * @private
     * @param {module:ui/dropdown/dropdownview~DropdownView} dropdown
     * @param {module:ui/view~View} form
     * @param {module:media-embed/mediaembedcommand~MediaEmbedCommand} command
     */
    _setUpForm(dropdown, form, command) {
        form.delegate('submit', 'cancel').to(dropdown);
        form.urlInputView.bind('value').to(command, 'value');

        // Form elements should be read-only when corresponding commands are disabled.
        form.urlInputView.bind('isReadOnly').to(command, 'isEnabled', value => !value);
    }
}

function getFormValidators(t, registry) {
    return [
        form => {
            if (!form.url.length) {
                return t('The must not be empty.');
            }
        },
    ];
}
